
import { node, createPremiumIcon } from "~/util/dom";
import { createHeader } from "~/profile/utils/header";
import { _ } from "~/i18n";
import createLinkButton from "~/ui/linkbutton";
import { store } from "~/state/store";
import { openPropositionDialog } from "~/util/premium";
import { IUserSpeedData, ESETTINGS_KEYS } from "~/state/user/interfaces";
import { watch, watchSettingsUserSpeedChange } from "~/state/util";
import { Unsubscribe } from "redux";
import { DEFAULT_VALUE_USER_SPEED } from "~/util/constants";
import { RESET_INPUT_24 } from "~/util/sds-compatible-icons";
import createSDSIcon from "~/ui/icon";

interface ITypeItems {
	container: HTMLLIElement;
	textValue: HTMLDivElement;
	editValue: {
		container: HTMLDivElement;
		input: HTMLInputElement;
		reset: HTMLSpanElement | SVGElement;
	};
}

interface IEditData {
	items: Array<ITypeItems>;
	buttonsContainer: HTMLDivElement;
	saveButton: HTMLButtonElement;
	editMessage: HTMLParagraphElement;
}

interface ISettingsUserSpeedProps {
	title: string;
	desc: string;
	change: (userSpeedData: IUserSpeedData) => void;
}

const MIN_VALUE = 1;
const MAX_VALUE = 999;
const CHANGED_CLASS = "changed";

function roundToTwoDecimals(number: number) {
	return Math.round(number * 100) / 100;
}

export default class SettingsUserSpeed {
	#container: HTMLDivElement;

	#settingsName: string;

	#userspeedTypeItems: Array<{
		criterions: Array<number>;
		title: string;
		default: number;
	}>;

	#editData: IEditData;

	#props: ISettingsUserSpeedProps;

	#settingsUserSpeedValue: IUserSpeedData;

	#storeUnsubscribeUser: Unsubscribe;

	constructor(props: ISettingsUserSpeedProps, userSettingsValue: IUserSpeedData) {
		this.#settingsName = "user-speed";
		this.#container = node("div", { className: `settings-${this.#settingsName}` });
		this.#props = props;
		this.#settingsUserSpeedValue = userSettingsValue;
		this.#userspeedTypeItems = [
			{
				criterions: [131, 132],
				title: _("profile.settings.userspeed.type1"),
				default: DEFAULT_VALUE_USER_SPEED[131].value,
			},
			{
				criterions: [134],
				title: _("profile.settings.userspeed.type11"),
				default: DEFAULT_VALUE_USER_SPEED[134].value,
			},
			{
				criterions: [133],
				title: _("profile.settings.userspeed.type10"),
				default: DEFAULT_VALUE_USER_SPEED[133].value,
			},
			{
				criterions: [121],
				title: _("profile.settings.userspeed.type6"),
				default: DEFAULT_VALUE_USER_SPEED[121].value,
			},
			{
				criterions: [123],
				title: _("profile.settings.userspeed.type2"),
				default: DEFAULT_VALUE_USER_SPEED[121].value,
			},
			{
				criterions: [122],
				title: _("profile.settings.userspeed.type3"),
				default: DEFAULT_VALUE_USER_SPEED[122].value,
			},
			{
				criterions: [124],
				title: _("profile.settings.userspeed.type7"),
				default: DEFAULT_VALUE_USER_SPEED[124].value,
			},
			{
				criterions: [125],
				title: _("profile.settings.userspeed.type8"),
				default: DEFAULT_VALUE_USER_SPEED[125].value,
			},
			{
				criterions: [141],
				title: _("profile.settings.userspeed.type4"),
				default: DEFAULT_VALUE_USER_SPEED[141].value,
			},
			{
				criterions: [142],
				title: _("profile.settings.userspeed.type9"),
				default: DEFAULT_VALUE_USER_SPEED[142].value,
			},
			{
				criterions: [144, 143],
				title: _("profile.settings.userspeed.type5"),
				default: DEFAULT_VALUE_USER_SPEED[144].value,
			},
		];

		this.#editData = null;

		this.#build();

		const wtUserSpeed = watch(store.getState, "user.settings", watchSettingsUserSpeedChange);

		this.#storeUnsubscribeUser = store.subscribe(wtUserSpeed(newValue => {
			const newUserSpeedData = newValue.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value || {};

			this.#settingsUserSpeedValueChange(newUserSpeedData);
		}));
	}


	destroy() {
		this.#container.innerHTML = "";
		this.#editData = null;
		this.#settingsUserSpeedValue = null;
		this.#storeUnsubscribeUser();
	}

	get container() {
		return this.#container;
	}

	#build() {
		const header = createHeader(this.#settingsName, this.#props.title);
		const user = store.getState().user.user;
		const titlePara = node("p", { className: "settings-initial-position__paragraph" }, this.#props.desc);
		const buttonsContainer = node("div", { className: "settings-initial-position__buttons" });
		const editMessage = node("p", { className: "settings-initial-position__paragraph-valid" }, _("profile.settings.userspeed.editMessage"));
		const saveBtn = node("button", { type: "button", className: "save", textContent: _("save") });
		const cancelBtn = node("button", { type: "button", className: "cancel", textContent: _("cancel") });

		saveBtn.setAttribute("data-log-ignore", "1");

		const editButton = createLinkButton({
			innerHTML: `${_("set")}`,
			...user.premium ? { className: "premium" } : {},
		});

		if (!user.premium) {
			editButton.prepend(createPremiumIcon());
		}

		editButton.addEventListener("click", () => {
			const actualUser = store.getState().user.user;

			if (actualUser.premium) {
				!this.#container.classList.contains("edit") && this.#showEdit();
			} else {
				Mapy.getComponent("logger").log("setCustomSpeeds_premiumPurchase_show");
				openPropositionDialog("setCustomSpeeds");
			}
		});

		header.append(editButton);

		buttonsContainer.append(saveBtn, cancelBtn);

		this.#editData = {
			buttonsContainer: buttonsContainer,
			saveButton: saveBtn,
			editMessage,
			items: [],
		};

		this.#container.append(header, titlePara, this.#createList(user.premium));

		saveBtn.addEventListener("click", () => {
			const userSpeed = this.#save();

			Mapy.getComponent("logger").log("set-user-speed", {
				changedCriterion: JSON.stringify(userSpeed),
			});
		});

		cancelBtn.addEventListener("click", () => {
			this.#resetAll();
			this.#showEdit();
		});
	}

	#createList(premium = false) {
		const container = node("ul");

		this.#userspeedTypeItems.forEach(item => {
			const itemContainer = node("li");
			const title = node("div", {}, item.title);
			const textValue = node("div");
			const isSetValue = premium && this.#settingsUserSpeedValue && this.#settingsUserSpeedValue[item.criterions[0].toString()];
			const editValue = this.#createEditContainer(
				isSetValue
					? this.#settingsUserSpeedValue[item.criterions[0].toString()]?.value
					: item.default,
				item.default,
				"km/h",
			);

			textValue.append(
				isSetValue
					? roundToTwoDecimals(this.#settingsUserSpeedValue[item.criterions[0].toString()]?.value).toString()
					: item.default.toString(),
				" ",
				node("i", {}, "km/h"),
			);

			itemContainer.append(title, textValue);

			this.#editData.items.push(
				{
					container: itemContainer,
					textValue,
					editValue,
				},
			);

			editValue.input.addEventListener("input", () => {
				this.#validation();
			});

			container.append(itemContainer);
		});

		this.#validation();

		return container;
	}

	#createEditContainer(value, defaultValue, unit) {
		const container = node("div");
		const input = node("input", {
			type: "number",
			min: MIN_VALUE.toString(),
			max: MAX_VALUE.toString(),
			step: "0.01",
			value: roundToTwoDecimals(value).toString(),
			inputMode: "numeric",
		});
		const reset = createSDSIcon({
			symbol: RESET_INPUT_24,
		});

		reset.classList.add("hide");
		reset.addEventListener("click", () => {
			input.value = defaultValue;

			this.#validation();
		});

		container.append(
			input,
			node("span", {}, unit),
		);

		return {
			container,
			input,
			reset,
		};
	}


	#showEdit() {
		const show = !this.#container.classList.contains("edit");

		if (show) {
			this.#editData.items.forEach(item => {
				item.textValue.replaceWith(item.editValue.container);
			});

			this.container.append(this.#editData.editMessage, this.#editData.buttonsContainer);
		} else {
			this.#editData.items.forEach(item => {
				item.editValue.container.replaceWith(item.textValue);
			});
			this.#editData.editMessage.remove();
			this.#editData.buttonsContainer.remove();
		}

		this.#container.classList[show ? "add" : "remove"]("edit");
	}

	#validation(itemIndex?: number) {
		const from = itemIndex === undefined ? 0 : itemIndex;
		const to = itemIndex === undefined ? this.#editData.items.length-1 : itemIndex;
		let error = false;

		if (from <= to) {
			for (let index = from; index < this.#editData.items.length; index++) {
				const item = this.#editData.items[index].editValue;
				const actualValue = parseFloat(item.input.value);
				const changed = actualValue !== this.#userspeedTypeItems[index].default;

				item.container.classList[changed ? "add" : "remove"](CHANGED_CLASS);

				if (changed) {
					item.container.prepend(item.reset);
				} else {
					item.reset.remove();
				}

				if (!error && !item.input.checkValidity()) {
					error = true;
				}
			}
		}

		this.#disableSaveButton(error);
	}

	#save() {
		const userSpeed: IUserSpeedData = {};

		this.#userspeedTypeItems.forEach((item, key) => {
			const editValue = this.#editData.items[key].editValue;

			if (editValue.container.classList.contains(CHANGED_CLASS)) {
				item.criterions.forEach(criterion => {
					userSpeed[criterion] = {
						value: parseFloat(editValue.input.value) || item.default,
					};
				});
			}
		});

		this.#showEdit();
		this.#props.change(userSpeed);

		return userSpeed;
	}

	#resetAll() {
		this.#userspeedTypeItems.forEach((item, key) => {
			this.#editData.items[key].editValue.input.value = item.default.toString();
			this.#editData.items[key].editValue.container.classList.remove(CHANGED_CLASS);
		});
	}

	#disableSaveButton(disabled) {
		this.#editData.saveButton.disabled = disabled;
	}

	#settingsUserSpeedValueChange(userSettingsValue) {
		this.#settingsUserSpeedValue = userSettingsValue;

		this.#userspeedTypeItems.forEach((item, key) => {
			const value = this.#settingsUserSpeedValue[item.criterions[0].toString()]?.value || item.default;

			this.#editData.items[key].textValue.innerHTML = "";
			this.#editData.items[key].textValue.append(value.toString(), " ", node("i", {}, "km/h"));

			this.#editData.items[key].editValue.input.value = value.toString();
		});
	}
}
